import { useMemo } from 'react'
import get from 'lodash/get'
import first from 'lodash/first'
import { useRouter } from 'next/router'
import { theme, grid } from '../../styles/theme'
import Section from '../Section'
import ResponsiveImage from '../ResponsiveImage'
import { createUseStyles } from '../../helpers/createStyles'
import cn from 'classnames'
import isEmpty from 'lodash/isEmpty'

import { setProductGalleryOpen, setGalleryIndex } from '../../store/layoutSlice'

const isLandscape = (image) => {
  const aspect = get(image, ['asset', 'metadata', 'dimensions', 'aspectRatio'])
  return aspect > 1
}

export default function ProductImageGrid ({ data, page }) {
  const styles = useStyles()
  const { hasBottomBorder } = data
  const { colors, customColors } = page
  const router = useRouter()
  const activeColor = useMemo(() => {
    const colorValue = get(router, ['query', 'color'])
    return colorValue ? colors.find(color => color.label === colorValue) || customColors?.find(color => color.label === colorValue) : first(colors)
  }, [router, colors])
  const imageGallery = useMemo(() => {
    const colorSpecificImages = get(activeColor, 'images') || []
    return colorSpecificImages
  }, [router, activeColor])
  if (isEmpty(imageGallery)) return null

  const hasGallery = !isEmpty(page.images)
  const setGalleryOpen = setProductGalleryOpen

  return (
    <Section className={styles.productImageGrid} grid>
      <div className={styles.container}>
        {imageGallery.map((img, i) => (
          <div key={img._key} className={cn(styles.imageContainer, { landscape: isLandscape(img) })}>
            <button className={cn(styles.imageButton, { disabled: !hasGallery })} onClick={() => { setGalleryOpen(true); setGalleryIndex(i + 1) }} disabled={!hasGallery}>
              <ResponsiveImage image={img} className={styles.image} aspect={isLandscape(img) ? 920 / 630 : 448 / 664} />
              {hasGallery &&
                <svg className={styles.galleryIcon} width="8" height="8" viewBox="0 0 8 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path fillRule="evenodd" clipRule="evenodd" d="M3.2653 0H1H0V1V3.34695H3.2653L3.2653 0ZM3.2653 4.7347H0V7V8H1H3.2653L3.2653 4.7347ZM4.65305 8V4.7347H8V7V8H7H4.65305ZM4.65305 3.34695V0H7H8V1V3.34695H4.65305Z" fill="white"/>
                </svg>
              }
            </button>
          </div>
        ))}
      </div>
      {hasBottomBorder && (
        <div className={styles.bottomBorder} />
      )}
    </Section>
  )
}

const useStyles = createUseStyles({
  productImageGrid: {},
  container: {
    gridColumn: '1 / -1',
    display: 'grid',
    gridTemplateColumns: `repeat(${grid.sm.columns}, minmax(0px, 1fr))`,
    gap: `${grid.sm.gutter}px`,
    rowGap: `${grid.md.gutter}px`,
    [theme.breakpoints.up('md')]: {
      gridColumn: '4 / -1',
      gap: `${grid.md.gutter}px`,
      gridTemplateColumns: 'repeat(8, minmax(0px, 1fr))'
    }
  },
  imageContainer: {
    gridColumn: 'span 4',
    '&.landscape': {
      gridColumn: 'span 8'
    }
  },
  image: {},
  bottomBorder: {
    display: 'none',
    gridColumn: '1 / -1',
    opacity: '0.1',
    borderBottom: '1px solid var(--foreground)',
    [theme.breakpoints.up('md')]: {
      display: 'block',
      marginTop: theme.spacingPx(8),
      gridColumn: '4 / -1'
    }
  },
  imageButton: {
    width: '100%',
    background: 'none',
    color: 'inherit',
    border: 'none',
    padding: 0,
    font: 'inherit',
    cursor: 'pointer',
    outline: 'inherit',
    position: 'relative'
  },
  galleryIcon: {
    position: 'absolute',
    right: 8,
    bottom: 8,
    zIndex: 2
  }
})
